/**
 * Federation Sports — canonical member-role constants.
 *
 * Single source of truth for role slugs so App.js, guards, page
 * components, tests, and API helpers stop hard-coding inline strings.
 * The slug values MUST match the authoritative role strings on the
 * backend `profiles.role` column — never change one without updating
 * the other.
 */

export const ROLES = Object.freeze({
  FRONT_OFFICE_STAFF: 'front_office_staff',
  TEAM_ADMIN: 'team_admin',
  MEMBER: 'member',
  PROSPECT: 'prospect',
  TEAM_ACTIVE_ATHLETE: 'team_active_athlete',
});

/**
 * Default landing surface per role. Used by `<ProtectedRoute>` to send
 * a signed-in user home when the current route is not allowed, and by
 * the login page to route the user to the right dashboard after a
 * successful sign-in.
 *
 * League Admin (front_office_staff + team_admin) is the single admin
 * source of truth — both admin roles land inside `/league-admin` so
 * the legacy `/team-admin` route stays consolidated.
 */
export const ROLE_DASHBOARDS = Object.freeze({
  [ROLES.FRONT_OFFICE_STAFF]: '/league-admin',
  [ROLES.TEAM_ADMIN]:         '/league-admin/prospects',
  [ROLES.PROSPECT]:           '/prospect',
  [ROLES.TEAM_ACTIVE_ATHLETE]: '/athlete',
  [ROLES.MEMBER]:             '/member',
});

/**
 * Human-facing label for each role — kept next to the slug so pages
 * (e.g. AccessDenied) never have to invent copy for a given role.
 */
export const ROLE_LABELS = Object.freeze({
  [ROLES.FRONT_OFFICE_STAFF]:  'Front Office Staff',
  [ROLES.TEAM_ADMIN]:          'Team Admin',
  [ROLES.MEMBER]:              'Member',
  [ROLES.PROSPECT]:            'Prospect',
  [ROLES.TEAM_ACTIVE_ATHLETE]: 'Active Athlete',
});

/** Returns true when the supplied role slug is one of ours. */
export const isKnownRole = (role) =>
  typeof role === 'string' && Object.values(ROLES).includes(role);

/** Resolves a landing route for a role, falling back to `/`. */
export const dashboardForRole = (role) =>
  ROLE_DASHBOARDS[role] || '/';

/** Friendly label for a role slug, falling back to a titlecased slug. */
export const labelForRole = (role) => {
  if (!role) return 'Guest';
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  return String(role)
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
};
